import React, { useCallback, useEffect, useState } from 'react';
import {
  getSalesServicePriceList,
  updateSalesServicePriceListItem,
} from '../../services/salesService';
import MutateGate from '../Common/MutateGate';

const perPage = 50;

const PriceListServicesSection = ({ activePosId, search = '', onToast }) => {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, per_page: perPage, total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const loadList = useCallback(async () => {
    if (!activePosId) {
      setItems([]);
      setMeta({ current_page: 1, last_page: 1, per_page: perPage, total: 0 });
      return;
    }
    setLoading(true);
    try {
      const res = await getSalesServicePriceList(Number(activePosId), search, page, perPage);
      if (res.success) {
        setItems(res.data || []);
        setMeta(res.meta || { current_page: page, last_page: 1, per_page: perPage, total: res.data?.length || 0 });
      }
    } catch (error) {
      onToast(error.response?.data?.message || 'Error al cargar servicios', 'error');
    } finally {
      setLoading(false);
    }
  }, [activePosId, search, page, onToast]);

  useEffect(() => {
    setPage(1);
  }, [activePosId, search]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const patchServiceRow = async (serviceId, payload) => {
    setSavingId(serviceId);
    try {
      const res = await updateSalesServicePriceListItem(serviceId, {
        point_of_sale_id: Number(activePosId),
        ...payload,
      });
      if (res.success) {
        setItems((prev) =>
          prev.map((row) => (row.id === serviceId ? { ...row, ...res.data } : row)),
        );
      }
    } catch (error) {
      onToast(error.response?.data?.message || 'Error al guardar', 'error');
    } finally {
      setSavingId(null);
    }
  };

  const handlePriceBlur = async (serviceId, rawValue, priceIncludesIgv) => {
    if (!activePosId) return;
    const trimmed = String(rawValue ?? '').trim();
    const price = trimmed === '' ? null : parseFloat(trimmed);
    if (trimmed !== '' && (Number.isNaN(price) || price < 0)) {
      onToast('Precio inválido', 'error');
      return;
    }
    await patchServiceRow(serviceId, {
      price,
      price_includes_igv: Boolean(priceIncludesIgv),
    });
  };

  const handleIgvModeChange = async (serviceId, includesIgv, currentPrice) => {
    if (!activePosId) return;
    await patchServiceRow(serviceId, {
      price: currentPrice === '' || currentPrice == null ? null : Number(currentPrice),
      price_includes_igv: includesIgv,
    });
  };

  if (!activePosId) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow-md text-gray-500">
        Elija un punto de venta para gestionar precios de servicios.
      </div>
    );
  }

  if (loading) {
    return <div className="text-center py-12 bg-white rounded-lg shadow-md text-gray-500">Cargando…</div>;
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow-md text-gray-500">
        No hay servicios de venta que coincidan con la búsqueda.
      </div>
    );
  }

  return (
    <>
      <p className="text-sm text-gray-500 mb-3">
        Indique si el monto ingresado es <strong>con IGV incluido</strong> o es la <strong>base imponible (+ IGV)</strong>{' '}
        al facturar.
      </p>
      <div className="bg-white rounded-lg shadow-md overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Servicio</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unidad</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IGV</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precio (S/)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {items.map((row) => {
              const includesIgv = row.price_includes_igv === true;
              return (
              <tr key={row.id} className="hover:bg-gray-50">
                <td className="px-4 py-4 text-sm font-mono text-teal-600">{row.sku || '—'}</td>
                <td className="px-4 py-4 text-sm text-gray-900">{row.name}</td>
                <td className="px-4 py-4 text-sm text-gray-600">
                  {row.unit_abbreviation || row.unit_of_measure || '—'}
                </td>
                <td className="px-4 py-4">
                  <MutateGate>
                    <select
                      value={includesIgv ? 'included' : 'plus'}
                      disabled={savingId === row.id}
                      onChange={(e) =>
                        handleIgvModeChange(row.id, e.target.value === 'included', row.price)
                      }
                      className="min-w-[9.5rem] px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="included">Incluido IGV</option>
                      <option value="plus">Más IGV</option>
                    </select>
                  </MutateGate>
                </td>
                <td className="px-4 py-4">
                  <MutateGate>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      defaultValue={row.price ?? ''}
                      key={`svc-p-${row.id}-${row.price ?? 'e'}-${includesIgv ? 'i' : 'p'}`}
                      disabled={savingId === row.id}
                      onBlur={(e) => handlePriceBlur(row.id, e.target.value, includesIgv)}
                      className="w-28 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-500"
                      placeholder="0.00"
                    />
                  </MutateGate>
                  <p className="text-[11px] text-gray-500 mt-1">
                    {includesIgv ? 'Precio final al cliente' : 'Base antes de IGV'}
                  </p>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {meta.last_page > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4 px-2">
          <p className="text-sm text-gray-600">
            Mostrando {(meta.current_page - 1) * meta.per_page + 1}–
            {Math.min(meta.current_page * meta.per_page, meta.total)} de {meta.total} servicios
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={meta.current_page <= 1 || loading}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50"
            >
              Anterior
            </button>
            <span className="text-sm text-gray-600">
              Página {meta.current_page} de {meta.last_page}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
              disabled={meta.current_page >= meta.last_page || loading}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PriceListServicesSection;
